import { createSeoAlternateLinks, createSeoCanonical, createSeoMeta } from '@mechhive/remix';
import { createStoryblokApi } from '~/api/storyblok.api.server';
import type { StoryblokDoc } from '~/api/types/storyblok/blocks/StoryblokDoc';
import type { StoryblokSimplePage } from '~/api/types/storyblok/pages/StoryblokSimplePage';
import { StoryblokRichText } from '~/components/storyblok/StoryblokRichText';
import { SubTitle } from '~/components/typograph/SubTitle';
import { Title } from '~/components/typograph/Title';
import { useLanguageServer } from '~/hooks/useLanguage.server';
import { META_LANGUAGES } from '~/locale';
import { createMetaUrl } from '~/utils/createMetaUrl';
import { getMetaBaseUrl } from '~/utils/getMetaBaseUrl';
import type { Route } from './+types/($lang).terms-and-conditions._index';

export const loader = async ( { params }: Route.LoaderArgs ) => {
  const language = useLanguageServer( params );

  const storyblokApi = createStoryblokApi();
  const storyResponse = await storyblokApi.getStory<StoryblokSimplePage>( 'pages/terms-and-conditions', language );

  return {
    story: storyResponse.data.story.content
  }
}

export const meta: Route.MetaFunction = ( { data, params, matches } ) => {
  const path = '/terms-and-conditions';
  const baseUrl = getMetaBaseUrl( matches );
  const url = createMetaUrl( {
    baseUrl,
    path,
    lang: params.lang
  } );

  return [
    ...createSeoMeta( {
      title: data?.story.metaFields.title,
      description: data?.story.metaFields.description,
      og: {
        title: data?.story.metaFields.title,
        description: data?.story.metaFields.description,
        url,
        image: 'https://cdn.rewarble.com/images/static/feature-rewarble-image.jpg'
      }
    } ),
    createSeoCanonical( {
      url
    } ),
    ...createSeoAlternateLinks( {
      baseUrl,
      defaultLocale: 'en',
      path,
      locales: META_LANGUAGES.map( m => ( {
        locale: m.lang,
        pathPrefix: m.pathPrefix ?? ''
      } ) )
    } )
  ]
}

export default function TermsAndConditions( { loaderData }: Route.ComponentProps ) {
  return (
    <div className={ 'container mx-auto px-4 flex flex-col gap-24 lg:gap-[100px] items-center' }>
      <div className={ 'md:w-[540px] flex flex-col items-center justify-center gap-[60px]' }>
        <div className={ 'flex flex-col gap-2.5 items-center' }>
          <Title headingLevel={ 1 }>
            { loaderData.story.title }
          </Title>
          <SubTitle className={ 'text-center' }>
            { loaderData.story.subTitle }
          </SubTitle>
        </div>
      </div>
      <div className={ 'w-full md:w-[680px]' }>
        <StoryblokRichText>
          { loaderData.story.content as StoryblokDoc }
        </StoryblokRichText>
      </div>
    </div>
  )
}
